import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useWatch } from "react-hook-form";
import TextField from "../Form/TextField";
import Select, { SELECT_MENU_ABOVE_MODAL_PROPS } from "../Form/Select";
import IconButton from "../Buttons/IconButton";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  MODAL_CREATE_LEAD_FORM_ADD_OPTION,
  MODAL_CREATE_LEAD_FORM_OPTION,
  MODAL_CREATE_LEAD_FORM_QUESTION_TITLE,
  MODAL_CREATE_LEAD_FORM_QUESTION_TYPE,
  MODAL_CREATE_LEAD_FORM_QUESTION_TYPE_MULTIPLE_CHOICE,
  MODAL_CREATE_LEAD_FORM_QUESTION_TYPE_TEXT,
} from "../../i18n/keysTranslations";
import {
  MAX_MULTIPLE_CHOICE_OPTIONS,
  META_INSTANT_FORM_FIELD_LIMITS,
  MIN_MULTIPLE_CHOICE_OPTIONS,
} from "../../utils/metaInstantForm";

const QUESTION_MAX_LENGTH = META_INSTANT_FORM_FIELD_LIMITS.question;

const QUESTION_TYPE_OPTIONS = [
  { value: "TEXT", label: MODAL_CREATE_LEAD_FORM_QUESTION_TYPE_TEXT },
  {
    value: "MULTIPLE_CHOICE",
    label: MODAL_CREATE_LEAD_FORM_QUESTION_TYPE_MULTIPLE_CHOICE,
  },
];

/** Matches filled single-line input height (theme minHeight 37px + vertical padding). */
const SINGLE_ROW_RESIZE_SX = {
  "& textarea": {
    resize: "vertical",
    minHeight: 37,
  },
};

const QUESTION_DELETE_COLUMN_SX = {
  width: 32,
  display: "flex",
  justifyContent: "flex-end",
  flexShrink: 0,
};

export const FormSection = ({ title, children, showDivider = true }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    {showDivider && <Divider />}
    <Typography variant="subtitle1" sx={{ pl: "2px", fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export const SectionTitleWithCounter = ({ title, count, max, sx }) => (
  <Typography variant="caption" color="text.secondary" sx={sx}>
    {title} {count}/{max}
  </Typography>
);

export const AddLinkButton = ({ onClick, disabled, children }) => (
  <Typography
    component="button"
    type="button"
    variant="caption"
    onClick={onClick}
    disabled={disabled}
    sx={{
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      background: "none",
      border: "none",
      p: 0,
      pl: "2px",
      cursor: disabled ? "default" : "pointer",
      textDecoration: "underline",
      color: disabled ? "text.disabled" : "text.secondary",
    }}
  >
    <AddIcon sx={{ fontSize: 14 }} />
    {children}
  </Typography>
);

export const FieldWithExternalLabel = ({ label, children, sx }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      ...sx,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ pl: "2px" }}>
      {label}
    </Typography>
    {children}
  </Box>
);

export const CustomQuestionFields = ({
  index,
  control,
  errors,
  onRemove,
  t,
  readOnly = false,
  setValue,
  getValues,
}) => {
  const questionType = useWatch({
    control,
    name: `customQuestions.${index}.type`,
  });
  const isMultipleChoice = questionType === "MULTIPLE_CHOICE";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pt: 1.5,
        px: 2,
        pb: 2,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 32,
        }}
      >
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
          {t(MODAL_CREATE_LEAD_FORM_QUESTION_TITLE)} {index + 1}
        </Typography>
        {!readOnly && (
          <Box sx={QUESTION_DELETE_COLUMN_SX}>
            <IconButton
              tooltipText=""
              onClick={onRemove}
              edge={false}
              sx={{ p: 0.5 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <FieldWithExternalLabel
          label={t(MODAL_CREATE_LEAD_FORM_QUESTION_TITLE)}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <TextField
            label=""
            name={`customQuestions.${index}.question`}
            variant="filled"
            multiline
            minRows={1}
            fullWidth
            control={control}
            errors={errors}
            sx={SINGLE_ROW_RESIZE_SX}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
              maxLength: {
                value: QUESTION_MAX_LENGTH,
                message: t(FIELD_MAX_LENGTH, { value: QUESTION_MAX_LENGTH }),
              },
            }}
          />
        </FieldWithExternalLabel>
        <Box sx={QUESTION_DELETE_COLUMN_SX} />
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <FieldWithExternalLabel
          label={t(MODAL_CREATE_LEAD_FORM_QUESTION_TYPE)}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Select
            label=""
            name={`customQuestions.${index}.type`}
            control={control}
            errors={errors}
            options={QUESTION_TYPE_OPTIONS}
            doTranslate={true}
            fullWidth
            variant="filled"
            disabled={readOnly}
            MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
          />
        </FieldWithExternalLabel>
        <Box sx={QUESTION_DELETE_COLUMN_SX} />
      </Box>
      {isMultipleChoice && (
        <MultipleChoiceOptions
          index={index}
          control={control}
          setValue={setValue}
          getValues={getValues}
          errors={errors}
          t={t}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
};

const MultipleChoiceOptions = ({
  index,
  control,
  setValue,
  getValues,
  errors,
  t,
  readOnly = false,
}) => {
  const optionsName = `customQuestions.${index}.options`;
  const options = useWatch({ control, name: optionsName }) || [];

  const updateOptions = (buildNext) => {
    const current = getValues(optionsName) || [];
    setValue(optionsName, buildNext(current), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {options.map((option, optionIndex) => (
        <Box
          key={`${index}-option-${optionIndex}`}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <FieldWithExternalLabel
            label={`${t(MODAL_CREATE_LEAD_FORM_OPTION)} ${optionIndex + 1}`}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TextField
              label=""
              name={`${optionsName}.${optionIndex}`}
              variant="filled"
              multiline
              minRows={1}
              fullWidth
              control={control}
              errors={errors}
              sx={{
                ...SINGLE_ROW_RESIZE_SX,
                "& textarea": {
                  resize: "vertical",
                  minHeight: 37,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                },
              }}
              rules={{
                required: {
                  value: true,
                  message: t(FIELD_REQUIRED),
                },
              }}
            />
          </FieldWithExternalLabel>
          {!readOnly && (
            <Box sx={{ ...QUESTION_DELETE_COLUMN_SX, pt: 2.5 }}>
              <IconButton
                tooltipText=""
                edge={false}
                disabled={options.length <= MIN_MULTIPLE_CHOICE_OPTIONS}
                onClick={() =>
                  updateOptions((current) =>
                    current.filter((_, itemIndex) => itemIndex !== optionIndex)
                  )
                }
                sx={{ p: 0.5 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      ))}
      {!readOnly && (
        <AddLinkButton
          onClick={() => updateOptions((current) => [...current, ""])}
          disabled={options.length >= MAX_MULTIPLE_CHOICE_OPTIONS}
        >
          {t(MODAL_CREATE_LEAD_FORM_ADD_OPTION)}
        </AddLinkButton>
      )}
    </Box>
  );
};
